<template>
  <svg class="design-svg" preserveAspectRatio="xMinYMin meet" :viewBox="`0 0 ${design.width} ${design.height}`">
    <defs>
      <clipPath :id="`${_uid}-clip-path`">
        <rect :width="design.width" :height="design.height" />
      </clipPath>
    </defs>

    <g :clip-path="`url(#${_uid}-clip-path)`">
      <template v-if="scalePattern">
        <g v-for="layer in scalePattern">
          <g v-for="block in layer" class="design-block" :key="block.key" :data-key="block.key"
            @click="$emit('block-click', { key: block.key, block })">
            <path :transform="getBlockTransform(block)" :d="block.path" :fill="getBlockFill(block)" />
          </g>
        </g>
      </template>

      <template v-else-if="sp402Pattern">
        <g v-for="block in sp402Pattern" class="design-block" :key="block.key" :data-key="block.key"
          @click="$emit('block-click', { key: block.key, block })">
          <path :transform="getBlockTransform(block)" :d="block.path" fill="#f5f5f5" />
        </g>
      </template>

      <template v-else>
        <g v-for="[key, block] in Object.entries(design.data)" class="design-block" :key="key"
          @click="$emit('block-click', { key, block })">
          <polygon v-if="brand === 'filzfelt' && block.points" :points="getBlockPoints(block)"
            :fill="getBlockFill(block)" />

          <path v-if="brand === 'filzfelt' && !block.points && !scaleBlockInUse" :transform="getBlockTransform(block)"
            :d="getFFBlockPath(block)" :fill="getBlockFill(block)" />

          <g v-if="brand === 'spinneybeck'" :transform="`translate(${block.x} ${block.y})`" v-draggable="!readonly"
            :data-drag="getBlockDropData(block)" @drag-start="blockDrag(key, $event)" @drag-end="blockDrop(key, $event)">
            <polygon points="0,0 16,0 16,16 0,16" fill="#eacfa2" />
            <path v-if="getBlockPath(block)" class="sp-path" :d="getBlockPath(block)"
              :transform="`rotate(${block.r || 0} 8 8)`" fill="none" />
          </g>
        </g>
      </template>
      <rect class="design-border" :width="design.width" :height="design.height" />
    </g>
  </svg>
</template>

<script>
import { mapGetters } from 'vuex'
import draggable from '../lib/v-draggable'
import COLORS from '../../universal/filzfelt/colors'
import FF_BLOCKS from '../../universal/filzfelt/blocks'
import SP_BLOCKS from '../../universal/spinneybeck/blocks'

export default {
  directives: {
    draggable
  },
  props: {
    design: {
      pattern: String,
      width: Number,
      height: Number,
      data: Object
    },
    readonly: Boolean
  },
  computed: {
    brand() {
      return this.$store.getters.brand
    },

    scaleBlockInUse() {
      const blockId = {
        'be3ee6b5-32fc-43ca-aa1d-d9a6919cfc70':
          '717fae39-bca4-4637-8e18-b203024ff3f2',
        'ce3a0a56-e73f-4f8c-bc6c-3c98453ce58f':
          'c52c3cb4-390b-4cac-9ff3-f601c7439369'
      }[this.design.pattern]
      return FF_BLOCKS[blockId]
    },

    scalePattern() {
      const BLOCK = this.scaleBlockInUse
      if (!BLOCK) return

      const bg = []
      const fg = []
      const data = this.design.data
      for (const key of Object.keys(data)) {
        const [i, j, k] = key.split(',').map(n => Number(n))
        const mask = (k === 0
          ? [
            data[`${i - 1},${j - 1},1`], // top-left
            data[`${i},${j - 1},1`], // top-right
            data[`${i},${j},1`], // bottom-right
            data[`${i - 1},${j},1`] // bottom-left
          ]
          : [
            data[`${i},${j},0`], // top-left
            data[`${i + 1},${j},0`], // top-right
            data[`${i + 1},${j + 1},0`], // bottom-right
            data[`${i},${j + 1},0`] // bottom-left
          ]
        )
          .map(a => {
            if (data[key].color) {
              return a && a.color ? 1 : 0
            } else {
              return a ? 1 : 0
            }
          })
          .join('')
        const path = BLOCK.variants[mask]
        const layer = data[key].color ? fg : bg
        layer.push({ ...data[key], key, path })
      }
      return [bg, fg]
    },

    ...mapGetters(['sp402Pattern'])
  },
  methods: {
    getBlockPoints({ points }) {
      if (points) return points.join(' ')
    },

    getFFBlockPath({ id }) {
      return FF_BLOCKS[id].path
    },

    getBlockTransform({ x, y, r }) {
      return (
        `translate(${x}, ${y})` +
        (r ? ` rotate(${r[0]}, ${r[1]}, ${r[2]})` : '')
      )
    },

    getBlockFill(block) {
      const { color } = block;
      const placeholderColor = FF_BLOCKS[block.id].placeholderColor ? FF_BLOCKS[block.id].placeholderColor : '#fff';
      const fill = COLORS[color]
      return (fill && fill.value) || placeholderColor
    },

    getBlockPath({ id }) {
      return SP_BLOCKS[id].d
    },

    getBlockDropData({ id, name, r }) {
      return JSON.stringify({ id, name, r })
    },

    blockDrop(key, event) {
      const { id, name, r } = JSON.parse(event.detail)
      this.$emit('block-drop', { key, id, name, r })
    },

    blockDrag(key, event) {
      const { id, name, r } = JSON.parse(event.detail)
      this.$emit('block-drag', { key, id, name, r })
      this.$nextTick(() => {
        // default/blank Lisboa tile
        const block = {
          id: '7d139b52-372d-4f39-93fc-40f3c0a37955',
          name: 'Lisboa 1'
        }
        this.$store.commit('setCustomBlock', { key, ...block })
      })
    }
  }
}
</script>

<style lang="postcss" scoped>
@import '../styles/vars.css';

.design-svg {
  display: block;
  max-height: 500px;
  user-select: none;
}

.design-block {
  stroke: var(--color-main);
  stroke-width: var(--stroke-width);

  & path.sp-path {
    stroke: var(--color-main);
    stroke-width: calc(var(--stroke-width) * 2);
  }
}

.active-color,
.active-custom-block,
.active-rotate {
  & .design-block {
    cursor: pointer;
  }
}

.design-border {
  fill: none;
  stroke: var(--color-main);
  stroke-width: calc(var(--stroke-width) * 2);
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// DesignView.vue?7030b57a